import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
const LearningMaps = lazy(() =>
  import('./components/LearningMaps.tsx').then((module) => ({ default: module.LearningMaps })),
);
import { migratePilotDates } from './storage.ts';
import { ExercisePanel } from './components/ExercisePanel.tsx';
import { ReadingPanel } from './components/Reading.tsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Home } from './components/Home.tsx';
import {
  availableDates as datesThroughToday,
  defaultDate,
  readMapCompletions,
  confirmSubjects,
  collectDailyStamp,
  navigationSubjects,
  subjectComplete,
} from './home.ts';
import { byId, findQuestion } from './content/index.ts';
import { dateForExercise, forDate } from './content/calendar.ts';
import { dismiss, localDay, streak, submit } from './domain.ts';
import {
  browserStorage,
  loadProgress,
  saveProgress,
  STORAGE_KEY,
  validProgress,
} from './storage.ts';
import { categories, labels, subjectLabels } from './types.ts';
import type { Category, Exercise, Progress, Question, Subject } from './types.ts';
import { log } from './logger.ts';

import { ConfirmDialog } from './components/ConfirmDialog.tsx';
import type { Notebook } from './notebook.ts';
import { freshNotebook } from './notebook.ts';
import { resetNotebook } from './notebook-actions.ts';
import { mapIndex } from './content/map-index.ts';
import type { MapState } from './maps.ts';

export type SharedState = {
  document: Notebook;
  generation: number;
  edit: (update: (document: Notebook) => Notebook, remount?: boolean) => void;
};

type ReadingView = { exerciseId: string; questionId?: string; full: boolean };
const storage = browserStorage();

export function App({
  shared,
  account,
  notice: accountNotice,
  blocked = false,
}: {
  shared?: SharedState;
  account?: ReactNode;
  notice?: ReactNode;
  blocked?: boolean;
}) {
  const [loaded] = useState(() =>
    shared
      ? { progress: freshNotebook().progress, error: null, writable: false }
      : loadProgress(storage),
  );
  const [localProgress, setLocalProgress] = useState(loaded.progress);
  const progress = shared?.document.progress ?? localProgress;
  const setProgress = (update: (value: Progress) => Progress) =>
    shared
      ? shared.edit((value) => ({ ...value, progress: update(value.progress) }))
      : setLocalProgress(update);
  const [storageError, setStorageError] = useState(loaded.error);
  const [today, setToday] = useState(localDay());
  const availableDates = datesThroughToday(today);
  const [category, setCategory] = useState<Category>('reason');
  const [page, setPage] = useState<'home' | 'exercise' | 'history' | 'maps'>('home');
  const [selectedDay, setSelectedDay] = useState(() => defaultDate(localDay()));
  const [reader, setReader] = useState<ReadingView | null>(null);
  const [notice, setNotice] = useState('');
  const [resetTarget, setResetTarget] = useState<{ subject?: Subject } | null>(null);
  const [localMapCompletions, setMapCompletions] = useState(() =>
    shared ? ({} as Record<string, boolean>) : readMapCompletions(storage),
  );
  const mapCompletions = shared
    ? readMapCompletions({
        getItem: (key) =>
          shared.document.maps[key] ? JSON.stringify(shared.document.maps[key]) : null,
      })
    : localMapCompletions;
  const mapCompletionChanged = useCallback((date: string, done: boolean) => {
    setMapCompletions((previous) =>
      previous[date] === done ? previous : { ...previous, [date]: done },
    );
  }, []);
  const visibleSubjects = navigationSubjects(progress, selectedDay);
  const main = useRef<HTMLElement>(null);
  const readingReturn = useRef({ scrollY: 0, focusId: '' });
  const applyingExternal = useRef(false);
  const writable = useRef(loaded.writable);
  const current = forDate(selectedDay, category);
  const context = `daily:${selectedDay}`;

  useEffect(() => {
    if (shared) return;
    if (applyingExternal.current) {
      applyingExternal.current = false;
      return;
    }
    if (writable.current) setStorageError(saveProgress(storage, progress));
  }, [progress]);
  useEffect(() => {
    const updateDay = () =>
      setToday((previous) => {
        const next = localDay();
        if (next !== previous)
          setSelectedDay((selected) =>
            selected === defaultDate(previous) ? defaultDate(next) : selected,
          );
        return next;
      });
    const timer = window.setInterval(updateDay, 30_000);
    window.addEventListener('focus', updateDay);
    const sync = (event: StorageEvent) => {
      if (shared) return;
      if (event.key === null || event.key?.startsWith('mentat.maps.v1:')) {
        setMapCompletions(readMapCompletions(storage));
      }
      if (event.key !== STORAGE_KEY) return;
      try {
        const next: unknown = JSON.parse(event.newValue ?? 'null');
        if (validProgress(next)) {
          applyingExternal.current = true;
          setLocalProgress(migratePilotDates(next));
          setStorageError(null);
          writable.current = true;
        } else {
          writable.current = false;
          setStorageError(
            'Saved progress changed in another tab. Reload before saving further changes; this tab can still be used for practice.',
          );
        }
      } catch {
        writable.current = false;
        setStorageError('Saved progress changed in another tab. Reload to reconnect it.');
      }
    };
    window.addEventListener('storage', sync);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', updateDay);
      window.removeEventListener('storage', sync);
    };
  }, []);
  useEffect(() => {
    setReader(null);
  }, [today]);
  useEffect(() => {
    // Rollover and cross-tab selection edits must not leave a hidden subject open.
    const active = page === 'maps' ? 'maps' : page === 'exercise' ? category : null;
    if (active && !navigationSubjects(progress, selectedDay).includes(active)) {
      setPage('home');
      setReader(null);
    }
  }, [progress.dailySubjects, progress.dailyConfirmed, selectedDay, page, category]);
  useEffect(() => {
    setPage('home');
    setReader(null);
  }, [selectedDay]);

  function navigate(next: Category) {
    if (!availableDates.includes(selectedDay)) return;
    setCategory(next);
    setPage('exercise');
    setReader(null);
    setNotice('');
  }
  function changeDate(date: string) {
    if (!availableDates.includes(date)) return;
    setSelectedDay(date);
    setPage('home');
    setReader(null);
    setNotice('');
    requestAnimationFrame(() => {
      main.current?.focus({ preventScroll: true });
      window.scrollTo(0, 0);
    });
  }
  function openHome() {
    setPage('home');
    setReader(null);
    setNotice('');
    window.scrollTo(0, 0);
  }
  function openSubject(subject: Subject) {
    if (subject === 'maps') {
      setPage('maps');
      setReader(null);
      setNotice('');
    } else navigate(subject);
    window.scrollTo(0, 0);
  }
  function read(exercise: Exercise, question?: Question) {
    readingReturn.current = {
      scrollY: window.scrollY,
      focusId: question ? `prompt-${question.id}` : '',
    };
    setReader({ exerciseId: exercise.id, questionId: question?.id, full: !question });
    main.current?.focus();
    window.scrollTo(0, 0);
  }
  function closeReading() {
    setReader(null);
    requestAnimationFrame(() => {
      const heading = document.getElementById(readingReturn.current.focusId);
      heading?.focus({ preventScroll: true });
      window.scrollTo(0, readingReturn.current.scrollY);
    });
  }
  function changeProgress(updater: (value: Progress) => Progress) {
    setProgress(updater);
  }

  function reset(subject?: Subject) {
    if (shared) setResetTarget({ subject });
  }

  const readerExercise = reader ? byId[reader.exerciseId] : null;
  const historyControl = (
    <button
      className={`history-link ${page === 'history' ? 'selected' : ''}`}
      onClick={() => {
        setPage('history');
        setReader(null);
      }}
    >
      History
      {progress.history.length > 0 && <span className="count">{progress.history.length}</span>}
    </button>
  );

  return (
    <>
      {shared && resetTarget && (
        <ConfirmDialog
          message={
            resetTarget.subject
              ? `Reset ${subjectLabels[resetTarget.subject]} for ${selectedDay}? Other subjects stay as they are.`
              : `Reset all progress and selections for ${selectedDay}? Other days stay as they are.`
          }
          action="Reset progress"
          onCancel={() => setResetTarget(null)}
          onConfirm={() => {
            shared.edit((value) => resetNotebook(value, selectedDay, resetTarget.subject), true);
            setReader(null);
            if (!resetTarget.subject) setPage('home');
            setResetTarget(null);
          }}
        />
      )}
      <a className="skip" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <a
          inert={blocked}
          className="wordmark"
          href="#"
          aria-label="Mentat Daily home"
          onClick={(event) => {
            event.preventDefault();
            openHome();
          }}
        >
          mentat daily
          <img
            className="wordmark-mouse"
            src="/brand/desert-mouse.webp"
            alt=""
            aria-hidden="true"
            width="192"
            height="192"
          />
          <span className="wordmark-period" aria-hidden="true">
            .
          </span>
        </a>
        <nav inert={blocked} aria-label="Daily sections" data-count={visibleSubjects.length}>
          {visibleSubjects.map((subject) => {
            const done = subjectComplete(
              progress,
              selectedDay,
              subject,
              !!mapCompletions[selectedDay],
            );
            const active =
              subject === 'maps' ? page === 'maps' : page === 'exercise' && category === subject;
            return (
              <button
                key={subject}
                disabled={!availableDates.length}
                aria-current={active ? 'page' : undefined}
                onClick={() => openSubject(subject)}
              >
                {subjectLabels[subject]}
                <span className="nav-tick" aria-label={done ? 'completed on this date' : undefined}>
                  {done ? '·' : ''}
                </span>
              </button>
            );
          })}
        </nav>
        {account}
      </header>
      {accountNotice}
      {storageError && (
        <p className="storage-warning" role="alert">
          {storageError}
        </p>
      )}
      <main id="main" ref={main} tabIndex={-1} inert={blocked} aria-busy={blocked}>
        {selectedDay !== today && page !== 'home' && (
          <p className="viewing-date">
            Viewing{' '}
            {new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(
              new Date(`${selectedDay}T12:00:00`),
            )}
            {!availableDates.includes(today) ? ' · No set is scheduled for today.' : ''}
          </p>
        )}
        {availableDates.length === 0 ? (
          <section>
            <h1>No practice published yet.</h1>
          </section>
        ) : reader && readerExercise ? (
          <ReadingPanel
            exercise={readerExercise}
            question={findQuestion(readerExercise, reader.questionId)}
            full={reader.full}
            onFull={() => setReader({ ...reader, full: true })}
            onBack={closeReading}
          />
        ) : page === 'home' ? (
          <Home
            key={`${selectedDay}:${shared?.generation ?? 0}`}
            date={selectedDay}
            today={today}
            progress={progress}
            mapDone={!!mapCompletions[selectedDay]}
            mapCompletions={mapCompletions}
            onDate={changeDate}
            onConfirm={(chosen) =>
              changeProgress((value) => confirmSubjects(value, selectedDay, chosen))
            }
            onStamp={() =>
              changeProgress((value) =>
                collectDailyStamp(value, selectedDay, !!mapCompletions[selectedDay]),
              )
            }
            onOpen={openSubject}
          />
        ) : page === 'maps' ? (
          <Suspense fallback={<p>Loading map…</p>}>
            <LearningMaps
              key={`${selectedDay}:${shared?.generation ?? 0}`}
              date={selectedDay}
              onCompletionChange={shared ? undefined : mapCompletionChanged}
              savedState={
                shared
                  ? (shared.document.maps[mapIndex[selectedDay].storageKey] ?? {
                      stage: -1,
                      solved: {},
                    })
                  : undefined
              }
              onStateChange={
                shared
                  ? (update: (value: MapState) => MapState) =>
                      shared.edit((value) => ({
                        ...value,
                        maps: {
                          ...value.maps,
                          [mapIndex[selectedDay].storageKey]: update(
                            value.maps[mapIndex[selectedDay].storageKey] ?? {
                              stage: -1,
                              solved: {},
                            },
                          ),
                        },
                      }))
                  : undefined
              }
              onReset={shared ? () => reset('maps') : undefined}
            />
          </Suspense>
        ) : page === 'history' ? (
          <>
            <div className="heading-row">
              <div>
                <h1>Reading history</h1>
              </div>
              {progress.history.length > 0 && (
                <span className="quiet">{progress.history.length} saved</span>
              )}
            </div>
            {progress.history.length === 0 ? (
              <div className="empty-state">
                <p>No saved readings.</p>
                <button onClick={openHome}>Back to my day</button>
              </div>
            ) : (
              <div className="history-list">
                {[...progress.history].reverse().map((row) => {
                  const exercise = byId[row.exerciseId];
                  const question = exercise && findQuestion(exercise, row.questionId);
                  if (!exercise || !question) return null;
                  return (
                    <div className="history-row" key={row.questionId}>
                      <button className="history-reading" onClick={() => read(exercise, question)}>
                        <span className="eyebrow">
                          {labels[exercise.category]} ·{' '}
                          {dateForExercise(exercise.id) ?? row.addedAt}
                        </span>
                        <span>{exercise.title}</span>
                        <span className="quiet history-prompt">{question.prompt}</span>
                      </button>
                      <button
                        className="dismiss"
                        aria-label={`Dismiss reading for ${question.id}`}
                        title="Dismiss reading"
                        onClick={() => {
                          changeProgress((value) => dismiss(value, row.questionId));
                          setNotice('Reading dismissed.');
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : current ? (
          <>
            <h1 className="exercise-title">{current.title}</h1>
            <ExercisePanel
              key={`${context}:${current.id}:${shared?.generation ?? 0}`}
              exercise={current}
              context={context}
              progress={progress}
              update={changeProgress}
              onRead={(question) => read(current, question)}
              onSubmit={(question, input) => {
                log('info', 'question.submitted', {
                  exerciseId: current.id,
                  questionId: question.id,
                  date: selectedDay,
                });
                changeProgress((value) =>
                  submit(value, current, question, input, context, selectedDay, today),
                );
              }}
            />
          </>
        ) : (
          <>
            <h1>No set published for this date yet.</h1>
            <div className="available-dates">
              {availableDates.map((date) => (
                <button key={date} onClick={() => changeDate(date)}>
                  {new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
                    new Date(`${date}T12:00:00`),
                  )}
                </button>
              ))}
            </div>
          </>
        )}
        <p className="sr-only" role="status">
          {notice}
        </p>
      </main>
      <footer inert={blocked}>
        <div className="utility-footer utility-header">
          {historyControl}
          {shared && !reader && (page === 'home' || page === 'exercise') && (
            <button
              className="history-link"
              onClick={() => reset(page === 'exercise' ? category : undefined)}
            >
              {page === 'home' ? 'Reset day' : 'Reset module'}
            </button>
          )}
        </div>
        <div>
          {categories.map((item) => {
            const count = streak(progress, today, item);
            return count > 0 ? (
              <span key={item}>
                {labels[item]} · {count} {count === 1 ? 'day' : 'days'}
              </span>
            ) : null;
          })}
          {streak(progress, today) > 0 && <span>All four · {streak(progress, today)} days</span>}
        </div>
      </footer>
    </>
  );
}
